import { Component } from 'react';
import { StyleProp, TextStyle, View } from 'react-native';

import IconQuestion from '../../../assets/svg/IconQuestion/IconQuestion';
import { neutralDark } from '../../../constants/color';
import { globalNormalDialogProxy } from '../../../elements/Dialog';
import NoScaleText from '../../../elements/NoScaleText';
import Price from '../../../elements/Price';
import Touchable from '../../../elements/Touchable';
import { translate } from '../../../i18n';
import { BillItem } from '../../../types/BillItem';
import { DeliveryMode } from '../../../types/Common';
import Currency, { CurrencySymbol } from '../../../types/Currency';
import { getIsTW } from '../../../utils/countryInfo';
import isExists from '../../../utils/isExists';
import { showNumberWithComma } from '../../../utils/stringHelper';
import styles from './BillDetailSection.style';
import { getHeaderSymbol } from './utils';

interface Props {
  currency: Currency;
  details: BillItem[];
  totalPriceTW: number;
  selectedMethod?: DeliveryMode;
  page?: 'checkout' | 'payment' | 'cart';
}

interface BillItemProps {
  item: BillItem;
  currencySymbol: CurrencySymbol;
  page?: 'checkout' | 'payment' | 'cart';
  notSelected?: boolean;
  priceValueStyle?: StyleProp<TextStyle>;
  currencyStyle?: StyleProp<TextStyle>;
}

export default class BillDetailSection extends Component<Props> {
  static defaultProps = {
    currency: {
      symbol: 'NT$',
      iso_code: 'TWD',
    },
  };

  static renderBillItem({
    item,
    currencySymbol,
    notSelected,
    priceValueStyle,
    currencyStyle,
    page,
  }: BillItemProps): JSX.Element | null {
    if (!item) {
      return null;
    }

    const OpenShippingFeeDialog = labelName => {
      const titleTextKey = (() => {
        switch (labelName) {
          case 'crossBorderShippingFee':
            return 'bill-detail-cross-border-shipping-hint-title';
          case 'eventVcoin':
            return 'bill-detail-event-vcoin-hint-title';
          default:
            return 'bill-detail-shipping-hint-title';
        }
      })();
      const positiveTextKey = (() => {
        switch (labelName) {
          case 'crossBorderShippingFee':
            return 'bill-detail-cross-border-shipping-hint-context';
          case 'eventVcoin':
            return 'bill-detail-event-vcoin-hint-context';
          default:
            return 'bill-detail-shipping-hint-context';
        }
      })();

      globalNormalDialogProxy.open({
        titleText: translate(titleTextKey),
        contentText: translate(positiveTextKey),
        positiveText: translate('got-it'),
      });
    };

    const showQuestionBtn =
      getIsTW() &&
      ['shippingFee', 'crossBorderShippingFee', 'eventVcoin'].includes(
        item.labelName
      ) &&
      page;

    return (
      <View key={`${item.label}-${item.labelMeta}`} style={styles.BillItem}>
        <View style={styles.BillLabel}>
          <NoScaleText style={[styles.lightGray, styles.normalFontStyle]}>
            {item.label}
          </NoScaleText>
          {showQuestionBtn && (
            <Touchable
              style={styles.iconQuestion}
              onPress={() => OpenShippingFeeDialog(item.labelName)}
            >
              <IconQuestion fillColor={neutralDark} size={16} />
            </Touchable>
          )}
        </View>
        <View style={styles.priceField}>
          {isExists(item.labelMeta) && (
            <NoScaleText style={styles.labelMeta}>{item.labelMeta}</NoScaleText>
          )}
          {item.labelName === 'shippingFee' && notSelected ? (
            <NoScaleText
              style={[styles.normalFontStyle, styles.notSelected]}
              testID="not-selected"
            >
              {translate('not-selected')}
            </NoScaleText>
          ) : (
            <Price
              price={item.value}
              currencyStyle={[
                styles.normalGray,
                currencyStyle || styles.normalFontStyle,
              ]}
              priceStyle={[
                styles.normalGray,
                priceValueStyle || styles.normalFontStyle,
              ]}
              currencySymbol={currencySymbol}
              headerSymbol={getHeaderSymbol(item.labelName)}
              allowFontScaling={false}
            />
          )}
        </View>
      </View>
    );
  }

  formatInfo = (details: BillItem[]) => {
    if (!details.length) {
      return false;
    }

    const theLast = details.slice(-1)[0];

    return {
      subItems: details.slice(0, -1),
      total: theLast,
    };
  };

  render() {
    const data = this.formatInfo(this.props.details);

    if (!data) {
      return false;
    }

    const { totalPriceTW, page, selectedMethod } = this.props;
    const { symbol: currencySymbol } = this.props.currency;
    const notSelected = page === 'cart' && !selectedMethod;
    const showCurrencyHint =
      currencySymbol !== CurrencySymbol.NT &&
      (page === 'payment' || page === 'checkout');

    return (
      <View
        style={[
          styles.billDetailSection,
          page === 'cart' && styles.cartPageStyle,
        ]}
        testID="billDetailSection"
      >
        <View style={styles.billDetail}>
          {data.subItems.map(item =>
            BillDetailSection.renderBillItem({
              item,
              page,
              currencySymbol,
              notSelected,
            })
          )}
          {BillDetailSection.renderBillItem({
            item: data.total,
            currencySymbol,
            notSelected,
            page,
            currencyStyle: styles.strongFontStyle,
            priceValueStyle: styles.strongFontStyle,
          })}
          {showCurrencyHint && (
            <NoScaleText style={styles.currencyHint}>
              {translate('bill-detail-TWD-price', {
                price: showNumberWithComma(totalPriceTW),
                symbol: CurrencySymbol.NT,
              })}
            </NoScaleText>
          )}
        </View>
      </View>
    );
  }
}
